import type { MouseEventHandler } from 'react'
import { Link } from 'react-router-dom'

import styles from '@/components/navigation/ActivityCard.module.scss'

type ActivityCardProps = {
  to: string
  title: string
  description: string
  cta: string
  images: string[]
  testId: string
  headingLevel?: 2 | 3
  layout?: 'featured' | 'game' | 'resource'
  imageFit?: 'contain' | 'cover'
  disabled?: boolean
  onClick?: MouseEventHandler<HTMLAnchorElement>
}

export default function ActivityCard({
  to,
  title,
  description,
  cta,
  images,
  testId,
  headingLevel = 3,
  layout = 'game',
  imageFit = layout === 'game' ? 'cover' : 'contain',
  onClick,
  disabled = false,
}: ActivityCardProps) {
  const Heading = headingLevel === 2 ? 'h2' : 'h3'

  return (
    <Link
      to={to}
      onClick={event => {
        if (disabled) event.preventDefault()
        else onClick?.(event)
      }}
      aria-disabled={disabled || undefined}
      className={`${styles.card} ${styles[layout]}`}
      data-testid={testId}
      aria-labelledby={`${testId}-title`}
      aria-describedby={`${testId}-description`}
    >
      <img
        key={images.join('|')}
        className={`${styles.image} ${imageFit === 'cover' ? styles.cover : ''}`}
        src={images[0]}
        alt=""
        onError={event => {
          const nextStage = Number(event.currentTarget.dataset.previewStage || '0') + 1
          if (!images[nextStage]) return
          event.currentTarget.dataset.previewStage = String(nextStage)
          event.currentTarget.src = images[nextStage]
        }}
      />
      <div className={styles.content}>
        <Heading className={styles.title} id={`${testId}-title`}>
          {title}
        </Heading>
        <p className={styles.description} id={`${testId}-description`}>
          {description}
        </p>
        <span className={styles.cta} aria-hidden="true">
          {cta} <span>→</span>
        </span>
      </div>
    </Link>
  )
}
